import {
  Task,
  Update,
  cases,
  defModule,
  ignore,
  transitions,
  type TaskDeclaration,
  type TaskMessage,
  type TransitionRow,
  type UpdateResult
} from '@st/redux'
import type { ClientContractTemplate, ClientOrganizerSettingsSchema, JSON } from '@st/sdk'
import { setToggle } from '@st/util/array-set'
import { putIn, updateIn } from '@st/util/update-in'
import { Effect } from 'effect'
import { Snackbar, sdk, type BrowserResourceRef, type SnackbarVariant } from '~/effects'
import { observeOrganizationRoute } from '~/features/module-runtime'
import {
  RETURN_TYPES,
  draftOf,
  folderSettingsRequest,
  openEngagementLetter,
  removeEngagementLetterRequest,
  saveFolderSettings,
  saveRequests,
  savedNotice,
  uploadEngagementLetter,
  type FolderSettings,
  type FolderSettingsDraft,
  type SaveResult,
  type SettingsScope
} from './folder-settings'

// ---------- model: the settings on screen, and writes left running ----------

export type ClientOrganizerSettingsState = {
  page: Page
  /** Settings left with a write in flight, by scope. Each comes back when its scope is shown. */
  background: Partial<Record<string, Writing>>
}

/** One scope's settings, from first load to the write in flight. */
type Page =
  | { status: 'waiting'; returnType: string }
  | { status: 'loading'; scope: SettingsScope }
  | { status: 'failed'; scope: SettingsScope }
  | { status: 'editing'; scope: SettingsScope; editor: Editor }
  | { status: 'writing'; scope: SettingsScope; editor: Editor; write: Write }

type Writing = Extract<Page, { status: 'writing' }>

type Editor = {
  schema: ClientOrganizerSettingsSchema
  /** What the server has. */
  persisted: FolderSettings
  /** What the person sees. Unsaved changes are the difference between the two. */
  draft: FolderSettingsDraft
  /** Whether the upload is offered; it matters only while no letter is stored. */
  letterChooserOpen: boolean
  expanded: string[]
}

type Write = 'settings' | 'uploadLetter' | 'removeLetter'

// ---------- messages ----------

export type ClientOrganizerSettingsMessage = Navigation | PageMessage

type Navigation =
  | TaskMessage<typeof observeSettingsRoute>
  | { type: 'returnTypeSelected'; returnType: string }

type PageMessage =
  // Loading
  | {
      type: 'loaded'
      scope: SettingsScope
      schema: ClientOrganizerSettingsSchema
      settings: FolderSettings
    }
  | { type: 'loadFailed'; scope: SettingsScope }
  | { type: 'retryRequested' }
  // The settings draft
  | { type: 'settingChanged'; key: string; value: JSON }
  | { type: 'flagChanged'; key: string; on: boolean }
  | { type: 'spouseSignatureChanged'; value: boolean }
  | { type: 'saveRequested' }
  | {
      type: 'saved'
      scope: SettingsScope
      results: SaveResult[]
      persisted: FolderSettings | undefined
    }
  // The engagement letter
  | { type: 'letterChooserToggled'; open: boolean }
  | { type: 'letterUploadRequested'; file: BrowserResourceRef<File> }
  | { type: 'letterUploaded'; scope: SettingsScope; template: ClientContractTemplate }
  | { type: 'letterUploadFailed'; scope: SettingsScope }
  | { type: 'letterRemoveRequested' }
  | { type: 'letterRemoved'; scope: SettingsScope }
  | { type: 'letterRemoveFailed'; scope: SettingsScope }
  | { type: 'letterPreviewRequested'; documentId: string }
  // The page's own view state
  | { type: 'categoryToggled'; categoryId: string }

type Message = ClientOrganizerSettingsMessage

// ---------- reads ----------

const observeSettingsRoute = observeOrganizationRoute({ name: 'client_organizer_settings' })

const loadFolderSettings = Task.define(
  (scope: SettingsScope) =>
    sdk.call(folderSettingsRequest(scope), {
      onSuccess: ({ schema, settings, plan }): Message => ({
        type: 'loaded',
        scope,
        schema,
        settings: { settings, plan }
      }),
      onFailure: (): Message => ({ type: 'loadFailed', scope })
    }),
  { key: scopeKey }
)

// ---------- one scope's settings ----------

type Result = UpdateResult<Page, Message>

/** Edits and view changes, accepted while editing and while a write is in flight. */
const edits = {
  settingChanged: (page, { key, value }) =>
    putIn(page, ['editor', 'draft', 'settings', key], value),
  // A flag is stored as true when on, and cleared with null when off.
  flagChanged: (page, { key, on }) =>
    putIn(page, ['editor', 'draft', 'settings', key], on ? true : null),
  spouseSignatureChanged: (page, { value }) =>
    putIn(page, ['editor', 'draft', 'plan', 'requireSpouseSignature'], value),
  letterChooserToggled: (page, { open }) => putIn(page, ['editor', 'letterChooserOpen'], open),
  letterPreviewRequested: (page, { documentId }) => [
    page,
    Effect.catchTag(
      openEngagementLetter(page.scope.organizationId, documentId),
      'FileStorageUrlTransientError',
      () => notify('Failed to preview engagement letter', 'error')
    )
  ],
  categoryToggled: (page, { categoryId }) =>
    updateIn(page, ['editor', 'expanded'], (expanded) => setToggle(expanded, categoryId))
} satisfies TransitionRow<Extract<Page, { editor: Editor }>, PageMessage, Result>

const updatePage = transitions<Page, PageMessage, 'status', Result>('status', {
  waiting: {},

  loading: {
    loaded: ({ scope }, { schema, settings }) => ({
      status: 'editing',
      scope,
      editor: {
        schema,
        persisted: settings,
        draft: draftOf(settings),
        letterChooserOpen: false,
        expanded: []
      }
    }),
    loadFailed: ({ scope }) => ({ status: 'failed', scope })
  },

  failed: {
    retryRequested: ({ scope }) => ({ status: 'loading', scope })
  },

  editing: {
    ...edits,
    saveRequested: (page) => {
      const { scope, editor } = page
      const requests = saveRequests(scope, editor.persisted, editor.draft)
      // Nothing differs from what the server has.
      if (requests.length == 0) return page
      return [
        { ...page, status: 'writing', write: 'settings' },
        Effect.map(
          saveFolderSettings(scope, requests),
          ({ results, persisted }): Message => ({ type: 'saved', scope, results, persisted })
        )
      ]
    },
    letterUploadRequested: (page, { file }) => [
      { ...page, status: 'writing', write: 'uploadLetter' },
      Effect.match(uploadEngagementLetter(page.scope, file), {
        onSuccess: (template): Message => ({ type: 'letterUploaded', scope: page.scope, template }),
        onFailure: (): Message => ({ type: 'letterUploadFailed', scope: page.scope })
      })
    ],
    letterRemoveRequested: (page) => [
      { ...page, status: 'writing', write: 'removeLetter' },
      sdk.call(removeEngagementLetterRequest(page.scope), {
        onSuccess: (): Message => ({ type: 'letterRemoved', scope: page.scope }),
        onFailure: (): Message => ({ type: 'letterRemoveFailed', scope: page.scope })
      })
    ]
  },

  writing: {
    ...edits,
    // A second click that lands before the page re-renders.
    ...ignore<PageMessage>('saveRequested', 'letterUploadRequested', 'letterRemoveRequested'),
    saved: ({ scope, editor }, { results, persisted }) => {
      const notice = savedNotice(scope.returnType, results)
      return [
        // Without a read-back the saved changes still look unsaved; saving them again is harmless.
        {
          status: 'editing',
          scope,
          editor: { ...editor, persisted: persisted ?? editor.persisted }
        },
        notify(notice.text, notice.variant)
      ]
    },
    letterUploaded: ({ scope, editor }, { template }) => ({
      status: 'editing',
      scope,
      editor: putIn(editor, ['persisted', 'plan', 'contractTemplate'], template)
    }),
    letterUploadFailed: ({ scope, editor }) => [
      { status: 'editing', scope, editor },
      notify('Failed to upload engagement letter', 'error')
    ],
    letterRemoved: ({ scope, editor }) => ({
      status: 'editing',
      scope,
      editor: {
        ...putIn(editor, ['persisted', 'plan', 'contractTemplate'], undefined),
        // With no letter stored, the chooser shows again; start it closed.
        letterChooserOpen: false
      }
    }),
    letterRemoveFailed: ({ scope, editor }) => [
      { status: 'editing', scope, editor },
      notify('Failed to remove engagement letter', 'error')
    ]
  }
})

// ---------- the module ----------

export const clientOrganizerSettingsModule = defModule<{
  state: ClientOrganizerSettingsState
  message: Message
}>({
  init: () => ({ page: { status: 'waiting', returnType: RETURN_TYPES[0] }, background: {} }),

  tasks: (model): TaskDeclaration<Message>[] => {
    if (model.page.status == 'loading') {
      return [observeSettingsRoute(), loadFolderSettings(model.page.scope)]
    }
    return [observeSettingsRoute()]
  },

  update: (model, message) => {
    switch (message.type) {
      case 'organizationRouteChanged':
        return show(model, activeOrganizationId(message.context), selectedReturnType(model.page))
      case 'returnTypeSelected':
        return show(model, currentOrganizationId(model.page), message.returnType)
      default:
        return route(model, message)
    }
  }
})

/**
 * Hands a message to the settings it is about. A message with a scope may be
 * an outcome for a write left in the background; its outcome ends that write,
 * and nothing on screen changes.
 */
function route(
  model: ClientOrganizerSettingsState,
  message: PageMessage
): UpdateResult<ClientOrganizerSettingsState, Message> {
  if (!('scope' in message) || isShowing(model.page, message.scope)) {
    return Update.mapState(updatePage(model.page, message), (page) => ({ ...model, page }))
  }
  const key = scopeKey(message.scope)
  const running = model.background[key]
  if (running == undefined) {
    throw new Error(`${message.type} for ${key}, which has no write in flight`)
  }
  return Update.mapState(updatePage(running, message), () => ({
    ...model,
    background: without(model.background, key)
  }))
}

/**
 * Shows the settings for `returnType` in the organization, or waits without
 * one. A write in flight moves to the background with its settings and comes
 * back with its scope; any other scope loads fresh.
 */
function show(
  model: ClientOrganizerSettingsState,
  organizationId: string | null,
  returnType: string
): ClientOrganizerSettingsState {
  const { page } = model
  if (organizationId != null && isShowing(page, { organizationId, returnType })) return model

  const background =
    page.status == 'writing'
      ? { ...model.background, [scopeKey(page.scope)]: page }
      : model.background
  if (organizationId == null) return { page: { status: 'waiting', returnType }, background }

  const scope = { organizationId, returnType }
  const key = scopeKey(scope)
  return {
    page: background[key] ?? { status: 'loading', scope },
    background: without(background, key)
  }
}

// ---------- what the page renders ----------

export type ClientOrganizerSettingsBody =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | ({ kind: 'form' } & ClientOrganizerSettingsForm)

export type ClientOrganizerSettingsForm = {
  scopeKey: string
  schema: ClientOrganizerSettingsSchema
  settings: FolderSettingsDraft['settings']
  letter: LetterView
  dirty: boolean
  saving: boolean
  expandedCategoryIds: string[]
}

/** The engagement letter section, derived from the stored letter, the chooser and the write. */
export type LetterView =
  | { status: 'off' }
  | { status: 'choosing' }
  | { status: 'uploading' }
  | { status: 'stored'; letter: ClientContractTemplate; requireSpouseSignature: boolean }
  | { status: 'removing'; letter: ClientContractTemplate; requireSpouseSignature: boolean }

export function selClientOrganizerSettingsPage(model: ClientOrganizerSettingsState) {
  return { returnType: selectedReturnType(model.page), body: pageBody(model.page) }
}

function pageBody(page: Page): ClientOrganizerSettingsBody {
  return cases(page, 'status', {
    waiting: () => ({ kind: 'loading' }),
    loading: () => ({ kind: 'loading' }),
    failed: () => ({ kind: 'failed' }),
    editing: ({ scope, editor }) => form(scope, editor, undefined),
    writing: ({ scope, editor, write }) => form(scope, editor, write)
  })
}

function form(
  scope: SettingsScope,
  editor: Editor,
  write: Write | undefined
): ClientOrganizerSettingsBody {
  return {
    kind: 'form',
    scopeKey: scopeKey(scope),
    schema: editor.schema,
    settings: editor.draft.settings,
    letter: letterView(editor, write),
    dirty: saveRequests(scope, editor.persisted, editor.draft).length > 0,
    saving: write != undefined,
    expandedCategoryIds: editor.expanded
  }
}

function letterView(editor: Editor, write: Write | undefined): LetterView {
  const letter = editor.persisted.plan.contractTemplate
  if (letter == undefined) {
    if (write == 'uploadLetter') return { status: 'uploading' }
    return editor.letterChooserOpen ? { status: 'choosing' } : { status: 'off' }
  }
  const { requireSpouseSignature } = editor.draft.plan
  if (write == 'removeLetter') return { status: 'removing', letter, requireSpouseSignature }
  return { status: 'stored', letter, requireSpouseSignature }
}

// ---------- scopes ----------

type RouteContext = Extract<Message, { type: 'organizationRouteChanged' }>['context']

/** Settings need a loaded organization and a signed-in user; anything else waits. */
function activeOrganizationId(context: RouteContext): string | null {
  if (context.status != 'loaded' || context.currentUserId == null) return null
  return context.organization.id
}

function currentOrganizationId(page: Page): string | null {
  return page.status == 'waiting' ? null : page.scope.organizationId
}

function selectedReturnType(page: Page): string {
  return page.status == 'waiting' ? page.returnType : page.scope.returnType
}

function isShowing(page: Page, scope: SettingsScope): boolean {
  return page.status != 'waiting' && scopeKey(page.scope) == scopeKey(scope)
}

function scopeKey(scope: SettingsScope): string {
  return `${scope.organizationId}:${scope.returnType}`
}

function without<Value>(record: Partial<Record<string, Value>>, key: string) {
  const rest = { ...record }
  delete rest[key]
  return rest
}

function notify(text: string, variant?: SnackbarVariant) {
  return Snackbar.use((snackbar) => {
    if (variant == undefined) return snackbar.show(text)
    return snackbar.show(text, { variant })
  })
}
